import React, { useState, useEffect, useCallback } from "react";
import { Modal, Typography, Autocomplete, Button, SelectCard } from "@ann/web-components";
import {
  AddGroupModalContent,
  ModalBodyNoPadding,
  GroupsSearchRow,
  GroupsSearchInputWrapper,
  GroupsSearchSummary,
  GroupsSelectionContainer,
  SelectedGroupsSummary,
  SelectedGroupsInfo,
  SelectedGroupsTitle,
  SelectedGroupsText,
  ClearGroupsSelectionText,
  LoadingContainer,
} from "./AddGroupModal.styled";
import { getGroupList } from "../../services";
import useDebounce from "../../../../../hooks/useDebounce";
import { useBackendPagination } from "../../hooks/useBackendPagination";
import { LoadMoreSection } from "../../../DrivesPage/components/LoadMoreSection";

const AddGroupModal = ({
  isOpen,
  onClose,
  searchValue,
  setSearchValue,
  selectedGroups,
  setSelectedGroups,
  onConfirmSelection,
}) => {
  // Debounce search value to avoid making requests on every keystroke
  const debouncedSearchValue = useDebounce(searchValue, 500);

  // Configure fetch function for getGroupList - memoized to avoid recreating on each render
  const fetchGroups = useCallback(async (params) => {
    try {
      const response = await getGroupList({
        page: params.page || 1,
        page_size: params.page_size || 8,
        order_by: params.order_by || ["created_at"],
        order_direction: params.order_direction || ["DESC"],
        search: params.search || null,
      });

      // Return in format expected by useBackendPagination
      return {
        body: response?.body || [],
        meta: response?.meta || {},
      };
    } catch (error) {
      console.error("Error fetching groups:", error);
      throw error;
    }
  }, []);

  // Use backend pagination hook
  const {
    items: allGroups,
    isLoading,
    error,
    hasMore,
    loadMore,
    reset,
    updateParams,
    meta,
  } = useBackendPagination(
    fetchGroups,
    {
      order_by: ["created_at"],
      order_direction: ["DESC"],
    },
    {
      itemsPerPage: 8,
      autoLoadFirstPage: false, // We'll control when to load
      onError: (error) => {
        console.error("Error loading groups:", error);
      },
    }
  );

  // Load groups when modal opens
  useEffect(() => {
    if (isOpen) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Update search params when search value changes (with debounce)
  useEffect(() => {
    if (!isOpen) return; // Don't search if modal is closed

    const timeoutId = setTimeout(() => {
      if (debouncedSearchValue && debouncedSearchValue.trim() !== "") {
        const keyword = debouncedSearchValue.trim();
        updateParams({ search: { keyword } });
        // Reset to page 1 when searching
        reset({ search: { keyword } });
      } else {
        updateParams({ search: undefined });
        // Reset to page 1 when clearing search
        reset({ search: undefined });
      }
    }, 0); // Already debounced, so no additional delay needed

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue, isOpen]);

  // Map API groups to modal format
  const groups = allGroups.map((group) => ({
    id: group.uuid || group.groupUuid, // API returns 'uuid', fallback to 'groupUuid' for compatibility
    title: group.name,
    description: group.description || "",
  }));

  const totalGroups = meta?.total_items || groups.length;
  const visibleGroups = groups.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="small">
      <Modal.Header>
        <Modal.Header.Title>Add groups</Modal.Header.Title>
      </Modal.Header>
      <ModalBodyNoPadding>
        <AddGroupModalContent>
          <Typography category="body" size="medium" weight="regular">
            Select groups to organize users by team, department, or location. Groups help manage access at scale.
          </Typography>
          <GroupsSearchRow>
            <GroupsSearchInputWrapper>
              <Autocomplete
                value={searchValue}
                onChange={setSearchValue}
                onSearch={() => {}}
                onSuggestionSelect={() => {}}
                onClear={() => setSearchValue("")}
                placeholder="Search groups"
                suggestions={[]}
                disabled={isLoading}
              />
            </GroupsSearchInputWrapper>
            <GroupsSearchSummary>
              Showing {visibleGroups} of {totalGroups} groups
            </GroupsSearchSummary>
          </GroupsSearchRow>
          <GroupsSelectionContainer>
            {isLoading ? (
              <LoadingContainer>
                <Typography category="body" size="medium" weight="regular" color="greyFour">
                  Loading groups...
                </Typography>
              </LoadingContainer>
            ) : (
              <>
                <SelectCard.Group
                  kind="checkbox"
                  value={selectedGroups}
                  onChange={setSelectedGroups}
                  direction="row"
                  itemsPerRow={2}
                >
                  {groups.map((group) => (
                    <SelectCard id={group.id} key={group.id}>
                      <SelectCard.Title value={group.title} />
                      <SelectCard.Content value={group.description} />
                    </SelectCard>
                  ))}
                </SelectCard.Group>
                {hasMore && (
                  <LoadMoreSection
                    hasMore={hasMore}
                    isLoading={false}
                    onLoadMore={loadMore}
                    skeletonType="folder"
                    skeletonCount={4}
                  />
                )}
              </>
            )}
          </GroupsSelectionContainer>
          {selectedGroups.length > 0 && (
            <SelectedGroupsSummary>
              <SelectedGroupsInfo>
                <SelectedGroupsTitle>
                  Selected groups ({selectedGroups.length})
                </SelectedGroupsTitle>
                <SelectedGroupsText>
                  {groups
                    .filter((group) => selectedGroups.includes(group.id))
                    .map((group) => group.title)
                    .join(" · ")}
                </SelectedGroupsText>
              </SelectedGroupsInfo>
              <ClearGroupsSelectionText type="button" onClick={() => setSelectedGroups([])} disabled={isLoading}>
                Clear Selection
              </ClearGroupsSelectionText>
            </SelectedGroupsSummary>
          )}
        </AddGroupModalContent>
      </ModalBodyNoPadding>
      <Modal.Actions align="end">
        <Button label="Cancel" onClick={onClose} type="secondary" size="medium" disabled={isLoading} />
        <Button
          label={`Add ${selectedGroups.length} groups`}
          onClick={() => {
            // Get complete group data for selected groups
            const selectedGroupsData = groups.filter((group) => selectedGroups.includes(group.id));
            // Map to API format
            const groupsDataForStorage = selectedGroupsData.map((group) => ({
              groupUuid: group.id,
              name: group.title,
              description: group.description,
            }));
            onConfirmSelection(selectedGroups, groupsDataForStorage);
            onClose();
          }}
          size="medium"
          type="primary"
          disabled={selectedGroups.length === 0 || isLoading}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default AddGroupModal;

